import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getProfessionsCountry } from 'api/shortlister'
import { researchSessionStore } from 'shared/stores/researchSessionStore'

export function useResearchCountry({
	career,
	subCategory,
	draftCategory,
	draftSubCategory,
	draftCountry,
	draftCountries,
	activeDegree,
	loadingDetails,
	loadingProfession,
	awaitingCatalogDefaults,
	professionsError,
	professionsLength,
	isValidCareer,
	isSelectedCountry,
	setIsSelectedCountry,
	sourcesSettled,
	courseMapError,
}) {
	const [countries, setCountries] = useState([])
	const [loadingCountries, setLoadingCountries] = useState(true)
	const [countriesError, setCountriesError] = useState(null)
	const isValidCountry = useRef(false)
	const countriesRequestGeneration = useRef(0)
	const [countriesRetryToken, setCountriesRetryToken] = useState(0)
	const [recCountry, setRecCountry] = useState(null)

	const resetCountries = useCallback(() => {
		countriesRequestGeneration.current += 1
		setCountries([])
		setLoadingCountries(true)
		isValidCountry.current = false
		setCountriesError(null)
	}, [])

	//Get Countries
	useEffect(() => {
		if (loadingDetails || loadingProfession || awaitingCatalogDefaults) {
			setLoadingCountries(true)
			return
		}

		if (!isValidCareer?.current || !career) {
			setCountries([])
			const expectCareerSoon =
				Boolean(subCategory) &&
				!professionsError &&
				professionsLength > 0 &&
				!career
			setLoadingCountries(expectCareerSoon)
			return
		}

		setLoadingCountries(true)
		setCountries([])
		setCountriesError(null)
		const gen = ++countriesRequestGeneration.current
		const careerForRequest = career

		const fetchCountries = async () => {
			try {
				const result = await getProfessionsCountry(careerForRequest)
				if (gen !== countriesRequestGeneration.current) return
				if (
					careerForRequest !==
					(researchSessionStore.draftDefaults?.career || '')
				) {
					return
				}
				const list = result?.data[0] || []
				setCountries(list)
				setCountriesError(null)
			} catch (error) {
				if (gen !== countriesRequestGeneration.current) return
				console.error('Error fetching countries:', error)
				setCountries([])
				setCountriesError(error)
			} finally {
				if (gen === countriesRequestGeneration.current) {
					setLoadingCountries(false)
				}
			}
		}

		fetchCountries()
	}, [
		career,
		loadingProfession,
		loadingDetails,
		awaitingCatalogDefaults,
		subCategory,
		professionsError,
		professionsLength,
		countriesRetryToken,
		isValidCareer,
	])

	// isValid country after change career: only update recCountry for UI; do not clear user's country when it's not in the new profession's list
	useEffect(() => {
		if (
			!draftCategory ||
			!draftSubCategory ||
			!career ||
			loadingCountries ||
			loadingDetails ||
			!isSelectedCountry ||
			!countries?.length
		)
			return

		const checkCountry = () => {
			const index = countries.findIndex(el => el.country === draftCountry)

			if (index >= 0) {
				if (recCountry !== index) {
					setRecCountry(index)
				}
			} else if (recCountry != null) {
				setRecCountry(null)
			}

			isValidCountry.current = true
		}

		checkCountry()
	}, [
		career,
		draftCategory,
		draftSubCategory,
		countries,
		loadingCountries,
		loadingDetails,
		isSelectedCountry,
		draftCountry,
		recCountry,
	])

	// Counseling default Country — only after catalog chain + countries success
	useEffect(() => {
		if (!sourcesSettled) return
		if (loadingDetails || loadingProfession || loadingCountries) return
		if (courseMapError || professionsError || countriesError) return
		if (!countries?.length) return
		if (draftCountry || draftCountries.length > 0) return

		const firstCountry = countries[0]?.country
		if (!firstCountry) return

		researchSessionStore.seedCountryIfEmpty({ country: firstCountry })
	}, [
		sourcesSettled,
		loadingDetails,
		loadingProfession,
		loadingCountries,
		courseMapError,
		professionsError,
		countriesError,
		countries,
		draftCountry,
		draftCountries.length,
	])

	const retryCountries = useCallback(() => {
		if (loadingCountries) return
		resetCountries()
		setCountriesRetryToken(token => token + 1)
	}, [loadingCountries, resetCountries])

	const countryCards = useMemo(() => {
		let list = countries || []
		if (list.length && activeDegree === 'Bachelors') {
			list = list.filter(
				el => !(el.country === 'New Zealand' || el.country === 'Germany'),
			)
		}
		return list.map((item, index) => ({
			id: String(item.country || index),
			title: item.country || '',
			isTopJobs: index === 0,
			salary:
				item.estimated_salary != null && item.estimated_salary !== ''
					? `$${Number(item.estimated_salary).toLocaleString('en-US', {
							maximumFractionDigits: 0,
						})}/yr`
					: '',
			jobs:
				item.job_numbers != null && item.job_numbers !== ''
					? Number(item.job_numbers).toLocaleString('en-US', {
							maximumFractionDigits: 0,
						})
					: '',
			region: item.region || item.visa_policies?.ideal_region || '',
			immigrationSummary: item.visa_policies?.post_study_visa
				? String(item.visa_policies.post_study_visa)
				: '',
			raw: item,
		}))
	}, [countries, activeDegree])

	return {
		countries,
		countryCards,
		loadingCountries,
		countriesError,
		resetCountries,
		retryCountries,
		isValidCountry,
		recCountry,
		setRecCountry,
	}
}
